/** Mirrors `HwcgActivationLogDto` served by coms `GET /dice/incidents/:id/hwcg-activation-log`. */

export type HwcgActivationSource = 'roster' | 'group';

export type HwcgActivationAttempt = {
  channel: string;
  status: string;
  destination: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  respondedAt: string | null;
  responseValue: string | null;
  error: string | null;
};

export type HwcgActivationNoticeEntry = {
  notificationId: string;
  parentNotificationId: string | null;
  step: 1 | 2;
  source: HwcgActivationSource;
  action: 'activate' | 'notify';
  positionId: string | null;
  positionName: string | null;
  groupId: string | null;
  title: string;
  script: string;
  sentAt: string | null;
  attempts: HwcgActivationAttempt[];
  response: string | null;
  respondedAt: string | null;
  acknowledgedAt: string | null;
};

export type HwcgActivationRosterStatus = 'activated' | 'declined' | 'pending';

export type HwcgActivationUserLog = {
  userGuid: string;
  name: string;
  email: string | null;
  isMember: boolean;
  positions: { positionId: string; positionName: string }[];
  status: HwcgActivationRosterStatus | null;
  activatedAt: string | null;
  declinedAt: string | null;
  activationNote: string | null;
  step1Response: string | null;
  step1RespondedAt: string | null;
  step2Response: string | null;
  step2RespondedAt: string | null;
  lastNoticeAt: string | null;
  notices: HwcgActivationNoticeEntry[];
};

export type HwcgActivationEventKind =
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'responded'
  | 'acknowledged'
  | 'activated'
  | 'declined';

export type HwcgActivationEvent = {
  at: string;
  kind: HwcgActivationEventKind;
  userGuid: string;
  name: string;
  step: 1 | 2 | null;
  source: HwcgActivationSource | null;
  channel: string | null;
  positionName: string | null;
  detail: string | null;
  notificationId: string | null;
};

export type HwcgActivationChannelTally = {
  sent: number;
  delivered: number;
  failed: number;
  responded: number;
};

export type HwcgActivationSummary = {
  members: number;
  activated: number;
  declined: number;
  pending: number;
  noticesSent: number;
  usersNoticed: number;
  step1: Record<string, number>;
  step2: Record<string, number>;
  channels: Record<string, HwcgActivationChannelTally>;
  medianMinutesToRespond: number | null;
};

export type HwcgActivationLogDto = {
  incidentId: string;
  generatedAt: string;
  summary: HwcgActivationSummary;
  users: HwcgActivationUserLog[];
  events: HwcgActivationEvent[];
};

// ─── Labels ──────────────────────────────────────────────────────────────────

const CHANNEL_LABELS: Record<string, string> = {
  inApp: 'In-app',
  email: 'Email',
  sms: 'SMS',
  voice: 'Voice',
  teams: 'Teams',
};

export function channelLabel(channel: string | null | undefined): string {
  if (!channel) return '—';
  return CHANNEL_LABELS[channel] ?? channel;
}

export function sourceLabel(source: HwcgActivationSource | null | undefined): string {
  if (source === 'roster') return 'Roster seat';
  if (source === 'group') return 'Group action';
  return '—';
}

export function stepLabel(step: 1 | 2 | null | undefined): string {
  if (step === 1) return 'Step 1 · Confidentiality';
  if (step === 2) return 'Step 2 · Activation notice';
  return '—';
}

export function rosterStatusLabel(
  status: HwcgActivationRosterStatus | null | undefined
): string {
  if (status === 'activated') return 'Activated';
  if (status === 'declined') return 'Declined';
  if (status === 'pending') return 'Pending';
  return 'Not on roster';
}

const ACTIVATED_LABELS = new Set(['EOC activated', 'Reported', 'Activated']);
const DECLINED_LABELS = new Set([
  'EOC cannot be activated',
  'Cannot report',
  'Declined',
]);

/** Deliveries store the option label; EOC and roster step 2 word the same answer differently. */
export function normalizeStep2Response(
  label: string | null | undefined
): string {
  const value = (label ?? '').trim();
  if (!value) return 'Awaiting';
  if (ACTIVATED_LABELS.has(value)) return 'Activated';
  if (DECLINED_LABELS.has(value)) return 'Declined';
  return value;
}

export function eventKindLabel(kind: HwcgActivationEventKind): string {
  switch (kind) {
    case 'sent':
      return 'Sent';
    case 'delivered':
      return 'Delivered';
    case 'failed':
      return 'Failed';
    case 'responded':
      return 'Replied';
    case 'acknowledged':
      return 'Acknowledged';
    case 'activated':
      return 'Activated';
    case 'declined':
      return 'Declined';
    default:
      return kind;
  }
}

export function formatLogTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatClock(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Derivations ─────────────────────────────────────────────────────────────

export type HwcgActivationLogFilters = {
  source?: HwcgActivationSource | 'all';
  status?: HwcgActivationRosterStatus | 'all';
  /** Only people who have a notice at this step. */
  step?: 1 | 2 | 'all';
  search?: string;
};

export function filterActivationUsers(
  users: HwcgActivationUserLog[],
  filters: HwcgActivationLogFilters
): HwcgActivationUserLog[] {
  const search = (filters.search ?? '').trim().toLowerCase();
  return users.filter((user) => {
    if (filters.status && filters.status !== 'all') {
      if (user.status !== filters.status) return false;
    }
    if (filters.source && filters.source !== 'all') {
      if (!user.notices.some((notice) => notice.source === filters.source)) {
        return false;
      }
    }
    if (filters.step && filters.step !== 'all') {
      if (!user.notices.some((notice) => notice.step === filters.step)) {
        return false;
      }
    }
    if (search) {
      const haystack = [
        user.name,
        user.email ?? '',
        ...user.positions.map((position) => position.positionName),
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
}

/** Users who hold (or were noticed for) one of the given positions. */
export function usersForPositions(
  users: HwcgActivationUserLog[],
  positionIds: ReadonlySet<string>
): HwcgActivationUserLog[] {
  if (positionIds.size === 0) return [];
  return users.filter(
    (user) =>
      user.positions.some((position) => positionIds.has(position.positionId)) ||
      user.notices.some(
        (notice) => notice.positionId && positionIds.has(notice.positionId)
      )
  );
}

export function usersByGuids(
  users: HwcgActivationUserLog[],
  userGuids: ReadonlySet<string>
): HwcgActivationUserLog[] {
  if (userGuids.size === 0) return [];
  return users.filter((user) => userGuids.has(user.userGuid));
}

/** Summary counts recomputed for a subset of users (position modal scopes to its occupants). */
export function summarizeActivationUsers(
  users: HwcgActivationUserLog[]
): Pick<
  HwcgActivationSummary,
  | 'members'
  | 'activated'
  | 'declined'
  | 'pending'
  | 'noticesSent'
  | 'usersNoticed'
  | 'step1'
  | 'step2'
> {
  const out = {
    members: 0,
    activated: 0,
    declined: 0,
    pending: 0,
    noticesSent: 0,
    usersNoticed: 0,
    step1: {} as Record<string, number>,
    step2: {} as Record<string, number>,
  };
  for (const user of users) {
    if (user.isMember) {
      out.members += 1;
      if (user.status === 'activated') out.activated += 1;
      else if (user.status === 'declined') out.declined += 1;
      else out.pending += 1;
    }
    if (user.notices.length === 0) continue;
    out.usersNoticed += 1;
    out.noticesSent += user.notices.length;
    const s1 = user.step1Response?.trim() || 'Awaiting';
    out.step1[s1] = (out.step1[s1] ?? 0) + 1;
    const hasStep2 = user.notices.some((notice) => notice.step === 2);
    const s2 = hasStep2 ? normalizeStep2Response(user.step2Response) : 'Awaiting';
    out.step2[s2] = (out.step2[s2] ?? 0) + 1;
  }
  return out;
}

/**
 * One line for the roster table: where this person is in the two-step flow,
 * independent of the roster status column beside it.
 */
export function latestActivationState(
  user: HwcgActivationUserLog | undefined
): { label: string; at: string | null } | null {
  if (!user || user.notices.length === 0) return null;
  const step2 = [...user.notices].reverse().find((notice) => notice.step === 2);
  if (step2) {
    if (step2.response) {
      return {
        label: normalizeStep2Response(step2.response) === 'Activated'
          ? 'Reported'
          : normalizeStep2Response(step2.response) === 'Declined'
            ? 'Cannot report'
            : step2.response,
        at: step2.respondedAt,
      };
    }
    return { label: 'Activation notice sent', at: step2.sentAt };
  }
  const step1 = [...user.notices].reverse().find((notice) => notice.step === 1);
  if (step1?.response) {
    return { label: 'Confidentiality agreed', at: step1.respondedAt };
  }
  const failed = step1?.attempts.every((attempt) =>
    ['failed', 'bounced', 'undeliverable'].includes(attempt.status)
  );
  if (step1 && failed && step1.attempts.length > 0) {
    return { label: 'Notice failed', at: step1.sentAt };
  }
  return { label: 'Notice sent, awaiting', at: step1?.sentAt ?? null };
}

export function indexUsersByGuid(
  users: HwcgActivationUserLog[] | undefined
): Record<string, HwcgActivationUserLog> {
  const out: Record<string, HwcgActivationUserLog> = {};
  for (const user of users ?? []) out[user.userGuid] = user;
  return out;
}
